import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { Button, Pill } from "../common/Primitives";
import { ShieldCheck, Sparkles, ExternalLink, Clock, Lock } from "lucide-react";

/**
 * DeepDDRequestCard — shown on Investor Marketplace startup detail.
 * Displays entitlement status and CTA:
 *   - No entitlement       → "Request Deep DD" (3 options)
 *   - payment_pending      → "Complete payment" (stub, Cashfree wiring pending)
 *   - requested            → "Waiting for admin approval"
 *   - active               → "Open Deep DD" (opens PDF)
 *   - expired / revoked    → re-request CTA
 */
export default function DeepDDRequestCard({ startupId, startupName }) {
  const [state, setState] = useState({ loading: true, entitlement: null, report: null, error: null });
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState("auto"); // 'auto' | 'one_off' | 'subscription'

  useEffect(() => {
    if (!startupId) return;
    (async () => {
      try {
        const r = await api.get(`/investor/deep-dd/${startupId}`);
        setState({ loading: false, entitlement: r.data?.entitlement, report: r.data?.report, error: null });
      } catch (e) {
        if (e?.response?.status === 402 || e?.response?.status === 404) {
          setState({ loading: false, entitlement: null, report: null, error: e.response?.status });
        } else {
          setState({ loading: false, entitlement: null, report: null, error: "err" });
        }
      }
    })();
  }, [startupId]);

  const request = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/investor/deep-dd/${startupId}/request`, { path: choice });
      if (r.data?.already_active) {
        toast.success("Deep DD access already active.");
      } else if (r.data?.path === "tier_bundled") {
        toast.success("Institutional tier — access unlocked instantly.");
      } else if (r.data?.status === "payment_pending") {
        toast.info(`One-off request created — ₹${r.data.price_inr.toLocaleString("en-IN")}. Complete payment to unlock.`);
      } else if (r.data?.path === "subscription") {
        toast.success(`Subscription used — ${r.data.remaining_quota} left this month.`);
      } else {
        toast.info("Request submitted — VenturaZ admin will approve shortly.");
      }
      // Refresh state.
      const s = await api.get(`/investor/deep-dd/${startupId}`).catch((e) => e);
      if (s?.data) {
        setState({ loading: false, entitlement: s.data.entitlement, report: s.data.report, error: null });
      } else {
        setState({ loading: false, entitlement: r.data.entitlement, report: null, error: null });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="border border-navy/10 bg-white rounded-lg p-4 animate-pulse text-sm text-ink-muted">Checking Deep DD access…</div>;
  }

  const ent = state.entitlement;
  const active = ent?.status === "active";

  return (
    <div className="border-2 border-emerald-600/40 bg-gradient-to-br from-white to-emerald-50/30 rounded-lg p-5" data-testid="deep-dd-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 mb-1 inline-flex items-center gap-1">
            <ShieldCheck size={11} /> PREMIUM DILIGENCE
          </div>
          <div className="font-heading text-ink text-lg">Deep Due Diligence Report</div>
          <div className="text-xs text-ink-soft mt-1">
            Corporate snapshot · Risk scorecard · Legal + Financial DD · Red-flag matrix — for <b>{startupName}</b>.
          </div>
        </div>
        {active && <Pill tone="success">Access active</Pill>}
        {ent?.status === "requested" && <Pill tone="warn">Awaiting approval</Pill>}
        {ent?.status === "payment_pending" && <Pill tone="gold">Payment pending</Pill>}
      </div>

      {active && state.report && (
        <div className="mt-3 flex items-center gap-2">
          <a href={`${API_BASE}/investor/deep-dd/${startupId}/pdf`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-700 text-white text-sm font-heading hover:bg-emerald-800"
            data-testid="deep-dd-open-pdf">
            <ExternalLink size={13} /> Open Deep DD PDF
          </a>
          {ent?.expires_at && (
            <span className="text-[11px] font-mono text-ink-muted inline-flex items-center gap-1">
              <Clock size={11} /> Expires {ent.expires_at.slice(0, 10)}
            </span>
          )}
        </div>
      )}

      {active && !state.report && (
        <div className="mt-3 text-sm text-ink-soft italic">
          Report not yet generated — VenturaZ team is preparing it. You&apos;ll be notified when it&apos;s ready.
        </div>
      )}

      {ent?.status === "requested" && (
        <div className="mt-3 text-sm text-ink-soft italic">
          Your request is in the review queue. Expect a decision within 24 business hours.
        </div>
      )}

      {ent?.status === "payment_pending" && (
        <div className="mt-3 space-y-2">
          <div className="text-sm text-ink-soft">
            Amount payable: <b>₹{(ent.price_inr || 9999).toLocaleString("en-IN")}</b> · 30-day access
          </div>
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Cashfree checkout coming shortly — please contact your VenturaZ manager to complete payment.
          </div>
        </div>
      )}

      {(!ent || ent.status === "expired" || ent.status === "revoked") && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-ink-muted mb-2 flex items-center gap-1">
            <Lock size={12} /> Choose access path
          </div>
          <div className="flex flex-wrap gap-2" data-testid="deep-dd-path-picker">
            {[
              { key: "auto", label: "Ask VenturaZ (admin approves)" },
              { key: "one_off", label: "One-off · ₹9,999" },
              { key: "subscription", label: "Use subscription quota" },
            ].map((opt) => (
              <button key={opt.key} onClick={() => setChoice(opt.key)}
                className={`px-3 py-1.5 rounded border text-xs font-heading ${choice === opt.key ? "bg-emerald-600 text-white border-emerald-600" : "border-navy/15 text-ink hover:bg-navy/[0.04]"}`}
                data-testid={`deep-dd-choice-${opt.key}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <Button variant="gold" onClick={request} disabled={busy} data-testid="deep-dd-request">
            <Sparkles size={13} className="inline mr-1" /> {busy ? "Submitting…" : "Request Deep DD"}
          </Button>
        </div>
      )}
    </div>
  );
}
